/* eslint-disable import/prefer-default-export */
import { provideLocal } from '@vueuse/core';
import { useConsolidatedLatchkey } from '@modules/latchkey/composables';

import { useProductType } from '@composables/useProductType';

import { useColor } from '@modules/color';
import { useSize } from '@modules/size';
import { useUpc } from '@modules/upc';
import { PRODUCT_PRICE_REPOSITORY } from '@modules/product/constants';
import { usePrice, useInitPrice, usePriceParser } from './mcl-utils';
/**
 * returns selected traits associated with the Product Id
 * @param {ProductId} id
 * @param {SelectedTraits.selectedColor | null} selectedColor
 * @param {SelectedTraits.selectedSize | null} selectedSize
 * @param {SelectedTraits.selectedUpcId | null} selectedUpcId
 *
 * @returns {{
 *  selectedColor: SelectedTraits.selectedColor,
 *  selectedSize: SelectedTraits.selectedSize,
 *  selectedUpcId: SelectedTraits.selectedUpcId
 * }}
 */
const productTraits = (id, selectedColor, selectedSize, selectedUpcId) => ({
  id,
  selectedColor: selectedColor ?? useColor()?.selectedColor,
  selectedSize: selectedSize ?? useSize()?.selectedSize,
  selectedUpcId: selectedUpcId ?? useUpc()?.selectedUpcId,
});

/**
 * Get selected price based on selected product traits (i.e. color, size, types, etc.)
 *
 * @description
 * This composable is dual brand friendly and handles getting price selection.
 *
 * USE CASES:
 *
 * 1) Consolidated Latchkey Use Case
 *   - DESCRIPTION: The composable is called to handel the price selection use case, when available
 *   - PARAMS USED:
 *     a) id (Product Id)
 * 2) Standard Use Case
 *   - DESCRIPTION:
 *   - PARAMS USED: Uses the standard methodology of initializing, parsing, and retrieving selected product prices.
 *     a) id (Product Id)
 *     b) product (Product Data)
 *
 * @param {{
 *  id: ProductId,
 *  product: import('vue').ComputedRef<Product>,
 *  selectedColor: SelectedTraits.selectedColor | null,
 *  selectedSize: SelectedTraits.selectedSize | null,
 *  selectedUpcId: SelectedTraits.selectedUpcId | null,
 * }}
 */
export const useProductPriceProvide = ({
  id, product, selectedColor = null, selectedSize = null, selectedUpcId = null,
// eslint-disable-next-line consistent-return
}) => {
  const { isConsolidatedLatchKey } = useProductType();

  if (isConsolidatedLatchKey) {
    /** USE CASE #1 * */
    const { selectedPrice } = useConsolidatedLatchkey();
    provideLocal(PRODUCT_PRICE_REPOSITORY, selectedPrice);
    return;
  }

  /** USE CASE #2 * */

  // set product price data cache
  useInitPrice({ id, data: usePriceParser(product) });
  // return selected price from the usePrice composable
  const { selectedPrice } = usePrice({
    id,
    ...productTraits(id, selectedColor, selectedSize, selectedUpcId),
  });

  provideLocal(PRODUCT_PRICE_REPOSITORY, selectedPrice);
};
